/* ==========================================================================
   Bossi | Geometry (sections/bossi-geometry.liquid)
   - Reads the numbers from the table, so the table in the theme editor is the only source.
   - Draws the frame from reach, stack, angles, stays and fork offset, to scale, and redraws it
     smoothly when the size changes. A second size can be drawn behind it to compare.
   - Pointing at a row (or a letter on the drawing) highlights that measurement.
   - Find your size suggests a size from the rider height ranges in the table.
   - Phones: the key numbers follow the chosen size, and "See full geometry" opens the table
     (every size) full screen; closing it puts the table back.
   ========================================================================== */

const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
const NS = 'http://www.w3.org/2000/svg';
const RADIUS = { road: 336, allroad: 350, gravel: 362 };
const NEEDED = ['reach', 'stack', 'ht_angle', 'st_angle', 'chainstay', 'bb_drop'];
const rad = (d) => (d * Math.PI) / 180;
const add = (a, b, k = 1) => ({ x: a.x + b.x * k, y: a.y + b.y * k });
const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
const unit = (a, b) => {
  const l = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  return { x: (b.x - a.x) / l, y: (b.y - a.y) / l };
};

class BossiGeometry extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.uid = `geo-${Math.random().toString(36).slice(2, 8)}`;
    this.cfg = JSON.parse(this.querySelector('[data-geo-config]')?.textContent || '{}');
    this.table = this.querySelector('[data-geo-table]');
    this.svg = this.querySelector('[data-geo-svg]');
    this.rows = [...this.table.querySelectorAll('tbody tr')];
    this.data = {};
    this.letters = {};
    for (const row of this.rows) {
      const m = row.dataset.measure;
      if (!m || m === 'none' || m === 'rider_height') continue;
      this.data[m] = [...row.querySelectorAll('td[data-col]')].map((td) => parseFloat(td.dataset.value));
      this.letters[m] = row.querySelector('.b-geo__letter')?.textContent.trim() || '';
    }
    this.size = Number(this.dataset.sizeIndex || 0);
    this.compare = null;

    this.querySelectorAll('[data-geo-size]').forEach((input) =>
      input.addEventListener('change', () => this.setSize(Number(input.value)))
    );
    this.compareSelect = this.querySelector('[data-geo-compare]');
    this.compareSelect?.addEventListener('change', () => {
      this.compare = this.compareSelect.value === '' ? null : Number(this.compareSelect.value);
      this.update(false);
    });
    for (const row of this.rows) {
      const on = () => this.highlight(row.dataset.measure);
      const off = () => this.highlight(null);
      // Mouse only: a thumb resting on a row to scroll should not light it up.
      row.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && on());
      row.addEventListener('focus', on);
      row.addEventListener('pointerleave', (e) => e.pointerType === 'mouse' && off());
      row.addEventListener('blur', off);
    }
    this.setupFinder();
    this.setupSheet();

    this.canDraw = Boolean(this.svg) && NEEDED.every((k) => this.data[k]?.every(Number.isFinite));
    if (!this.canDraw) this.querySelector('.b-geo__drawing')?.setAttribute('hidden', '');
    else this.frameBox();
    this.shown = this.values(this.size);
    this.update(false);
  }

  values(i) {
    const out = {};
    for (const k in this.data) out[k] = this.data[k][i];
    return out;
  }

  setSize(i) {
    if (!Number.isFinite(i)) return;
    this.size = i;
    const radio = this.querySelector(`[data-geo-size][value="${i}"]`);
    if (radio && !radio.checked) radio.checked = true;
    this.update(true);
  }

  setCompare(i) {
    this.compare = i;
    if (this.compareSelect) this.compareSelect.value = i === null ? '' : String(i);
  }

  /* ---------- Table ---------- */

  update(animate) {
    const comparing = this.compare !== null && this.compare !== this.size;
    this.table.querySelectorAll('[data-col]').forEach((cell) => {
      const col = Number(cell.dataset.col);
      cell.classList.toggle('is-active', col === this.size);
      cell.classList.toggle('is-compare', comparing && col === this.compare);
    });
    this.classList.toggle('is-comparing', comparing);
    for (const row of this.rows) {
      const cell = row.querySelector('[data-delta]');
      if (!cell) continue;
      const cells = row.querySelectorAll('td[data-col]');
      const a = parseFloat(cells[this.size]?.dataset.value);
      const b = comparing ? parseFloat(cells[this.compare]?.dataset.value) : NaN;
      if (!Number.isFinite(a) || !Number.isFinite(b)) {
        cell.textContent = '';
        continue;
      }
      const d = Math.round((b - a) * 100) / 100;
      const u = row.dataset.unit === 'deg' ? '°' : row.dataset.unit === 'g' ? 'g' : '';
      cell.textContent = d === 0 ? '0' : `${d > 0 ? '+' : '−'}${Math.abs(d)}${u}`;
    }
    this.querySelectorAll('[data-geo-figure]').forEach((fig) => {
      const row = this.rows.find((r) => r.dataset.measure === fig.dataset.geoFigure);
      const cell = row?.querySelectorAll('td[data-col]')[this.size];
      const out = fig.querySelector('[data-geo-figure-value]');
      if (!cell || !out) return;
      const text = (cell.dataset.range ?? cell.dataset.value ?? '').replace('-', '–');
      if (out.textContent === text) return;
      out.textContent = text;
      if (animate && !reduce.matches) {
        out.animate([{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'none' }], {
          duration: 420,
          easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
        });
      }
    });
    const caption = this.querySelector('[data-geo-caption]');
    if (caption) {
      const sizes = this.cfg.sizes || [];
      caption.textContent = `${this.cfg.sizeLabel || 'Size'} ${sizes[this.size] ?? ''}` + (comparing ? `  ·  ${sizes[this.compare] ?? ''}` : '');
    }
    if (!this.canDraw) return;

    const target = this.values(this.size);
    const ghost = comparing ? this.values(this.compare) : null;
    cancelAnimationFrame(this.raf);
    if (!animate || reduce.matches) {
      this.shown = target;
      this.draw(target, ghost);
      return;
    }
    const from = { ...this.shown };
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / 700);
      const e = 1 - Math.pow(1 - p, 3);
      const cur = {};
      for (const k in target) cur[k] = Number.isFinite(from[k]) ? from[k] + (target[k] - from[k]) * e : target[k];
      this.shown = cur;
      this.draw(cur, ghost);
      if (p < 1) this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  highlight(measure) {
    const m = measure && measure !== 'none' ? measure : null;
    this.hl = m;
    for (const row of this.rows) row.classList.toggle('is-hl', Boolean(m) && row.dataset.measure === m);
    if (!this.svg) return;
    this.svg.classList.toggle('has-hl', Boolean(m) && Boolean(this.svg.querySelector(`[data-dim="${m}"]`)));
    this.svg.querySelectorAll('[data-dim]').forEach((g) => g.classList.toggle('is-hl', g.dataset.dim === m));
  }

  /* ---------- Drawing ---------- */

  points(v) {
    const R = RADIUS[this.dataset.wheel] || RADIUS.road;
    const drop = v.bb_drop;
    const ha = rad(v.ht_angle);
    const sa = rad(v.st_angle);
    const BB = { x: 0, y: 0 };
    const RA = { x: -Math.sqrt(Math.max(v.chainstay ** 2 - drop ** 2, 0)), y: drop };
    const T = { x: v.reach, y: v.stack };
    const down = { x: Math.cos(ha), y: -Math.sin(ha) };
    const htl = Number.isFinite(v.ht_length) ? v.ht_length : 140;
    const B = add(T, down, htl);
    const off = Number.isFinite(v.fork_offset) ? v.fork_offset : 45;
    const axis = { x: T.x + (T.y - drop) / Math.tan(ha), y: drop };
    const FA = { x: axis.x + off / Math.sin(ha), y: drop };
    const up = { x: -Math.cos(sa), y: Math.sin(sa) };
    const stl = Number.isFinite(v.st_length) ? v.st_length : v.stack / Math.sin(sa);
    const S = add(BB, up, stl);
    const E = { x: -v.stack / Math.tan(sa), y: v.stack };
    const seatAtDrop = { x: -drop / Math.tan(sa), y: drop };
    // Foot of the fork offset: the point on the steering axis closest to the front axle.
    const t = (FA.x - T.x) * down.x + (FA.y - T.y) * down.y;
    const foot = add(T, down, t);
    return { R, drop, ha, sa, BB, RA, T, B, FA, S, E, up, down, axis, seatAtDrop, foot };
  }

  // A fixed frame fitted to the largest size, so changing size visibly grows or shrinks the bike.
  frameBox() {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    const n = this.data.reach.length;
    for (let i = 0; i < n; i++) {
      const p = this.points(this.values(i));
      minX = Math.min(minX, p.RA.x - p.R, p.E.x);
      maxX = Math.max(maxX, p.FA.x + p.R, p.T.x + 230);
      minY = Math.min(minY, p.drop - p.R - 120);
      maxY = Math.max(maxY, p.T.y + 190, p.S.y + 40);
    }
    const pad = 30;
    this.box = { minX: minX - pad, maxX: maxX + pad, minY: minY - pad, maxY: maxY + pad };
    const w = this.box.maxX - this.box.minX;
    const h = this.box.maxY - this.box.minY;
    this.svg.setAttribute('viewBox', `0 0 ${Math.round(w)} ${Math.round(h)}`);
  }

  draw(v, ghost) {
    const svg = this.svg;
    const X = (p) => p.x - this.box.minX;
    const Y = (p) => this.box.maxY - p.y;
    const el = (tag, attrs, parent) => {
      const node = document.createElementNS(NS, tag);
      for (const k in attrs) node.setAttribute(k, attrs[k]);
      (parent || svg).append(node);
      return node;
    };
    const line = (a, b, cls, parent) => el('line', { x1: X(a), y1: Y(a), x2: X(b), y2: Y(b), class: cls }, parent);
    const path = (d, cls, parent) => el('path', { d, class: cls }, parent);
    const label = (p, text, parent) => {
      if (!text) return;
      el('circle', { cx: X(p), cy: Y(p), r: 27, class: 'g-tag' }, parent);
      const t = el('text', { x: X(p), y: Y(p), class: 'g-tag-text', 'text-anchor': 'middle', 'dominant-baseline': 'central' }, parent);
      t.textContent = text;
    };
    const arc = (c, r, a1, a2) => {
      const p1 = { x: c.x + r * Math.cos(a1), y: c.y + r * Math.sin(a1) };
      const p2 = { x: c.x + r * Math.cos(a2), y: c.y + r * Math.sin(a2) };
      const sweep = a2 > a1 ? 0 : 1;
      return `M${X(p1)} ${Y(p1)} A${r} ${r} 0 0 ${sweep} ${X(p2)} ${Y(p2)}`;
    };

    svg.replaceChildren();
    const defs = el('defs', {});
    const marker = el('marker', { id: `${this.uid}-arrow`, viewBox: '0 0 10 10', refX: 9, refY: 5, markerWidth: 7, markerHeight: 7, orient: 'auto-start-reverse' }, defs);
    el('path', { d: 'M0 1 L9 5 L0 9', class: 'g-arrow' }, marker);
    const arrow = `url(#${this.uid}-arrow)`;

    const bike = (p, cls) => {
      const g = el('g', { class: cls });
      el('circle', { cx: X(p.RA), cy: Y(p.RA), r: p.R, class: 'g-tyre' }, g);
      el('circle', { cx: X(p.FA), cy: Y(p.FA), r: p.R, class: 'g-tyre' }, g);
      el('circle', { cx: X(p.RA), cy: Y(p.RA), r: p.R - 34, class: 'g-rim' }, g);
      el('circle', { cx: X(p.FA), cy: Y(p.FA), r: p.R - 34, class: 'g-rim' }, g);
      const topJoin = add(p.T, p.down, 18);
      const downJoin = add(p.B, p.down, -26);
      const stayJoin = add(p.S, p.up, -34);
      const forkBend = { x: p.axis.x, y: p.drop + 70 };
      line(p.BB, p.RA, 'g-tube g-stay', g);
      line(stayJoin, p.RA, 'g-tube g-stay', g);
      line(p.BB, p.S, 'g-tube', g);
      line(p.S, topJoin, 'g-tube', g);
      line(p.BB, downJoin, 'g-tube g-down', g);
      path(`M${X(p.B)} ${Y(p.B)} Q${X(forkBend)} ${Y(forkBend)} ${X(p.FA)} ${Y(p.FA)}`, 'g-tube g-fork', g);
      line(p.T, p.B, 'g-tube g-head', g);
      el('circle', { cx: X(p.BB), cy: Y(p.BB), r: 21, class: 'g-bb' }, g);
      el('circle', { cx: X(p.RA), cy: Y(p.RA), r: 7, class: 'g-axle' }, g);
      el('circle', { cx: X(p.FA), cy: Y(p.FA), r: 7, class: 'g-axle' }, g);
      return g;
    };

    const p = this.points(v);
    if (ghost) bike(this.points(ghost), 'g-bike g-ghost');

    // Reference lines: axle line, steering axis, seat line.
    const refs = el('g', { class: 'g-refs' });
    line({ x: p.RA.x, y: p.drop }, { x: p.FA.x + 40, y: p.drop }, 'g-ref', refs);
    line(p.T, add(p.axis, p.down, 30), 'g-ref', refs);
    line(p.seatAtDrop, add(p.S, p.up, 30), 'g-ref', refs);

    bike(p, 'g-bike');

    const dims = el('g', { class: 'g-dims' });
    const dim = (measure, build) => {
      if (!Number.isFinite(v[measure])) return;
      const g = el('g', { class: 'g-dim', 'data-dim': measure }, dims);
      build(g);
      g.addEventListener('pointerenter', () => this.highlight(measure));
      g.addEventListener('pointerleave', () => this.highlight(null));
    };
    const L = this.letters;

    dim('ett', (g) => {
      const y = p.T.y + 60;
      line({ x: p.E.x, y: p.T.y }, { x: p.E.x, y: y + 16 }, 'g-ext', g);
      line(p.T, { x: p.T.x, y: y + 16 }, 'g-ext', g);
      line({ x: p.E.x, y }, { x: p.T.x, y }, 'g-line', g).setAttribute('marker-start', arrow);
      g.lastChild.setAttribute('marker-end', arrow);
      label({ x: (p.E.x + p.T.x) / 2, y: y + 40 }, L.ett, g);
    });
    dim('reach', (g) => {
      const y = p.T.y + 140;
      line(p.BB, { x: 0, y: y + 16 }, 'g-ext', g);
      line(p.T, { x: p.T.x, y: y + 16 }, 'g-ext', g);
      const l = line({ x: 0, y }, { x: p.T.x, y }, 'g-line', g);
      l.setAttribute('marker-start', arrow);
      l.setAttribute('marker-end', arrow);
      label({ x: p.T.x / 2, y: y + 40 }, L.reach, g);
    });
    dim('stack', (g) => {
      const x = p.T.x + 170;
      line({ x: 40, y: 0 }, { x: x + 16, y: 0 }, 'g-ext', g);
      line(p.T, { x: x + 16, y: p.T.y }, 'g-ext', g);
      const l = line({ x, y: 0 }, { x, y: p.T.y }, 'g-line', g);
      l.setAttribute('marker-start', arrow);
      l.setAttribute('marker-end', arrow);
      label({ x: x + 44, y: p.T.y / 2 }, L.stack, g);
    });
    dim('ht_length', (g) => {
      const n = { x: Math.sin(p.ha), y: Math.cos(p.ha) };
      const a = add(p.T, n, 62);
      const b = add(p.B, n, 62);
      line(add(p.T, n, 20), add(a, n, 14), 'g-ext', g);
      line(add(p.B, n, 20), add(b, n, 14), 'g-ext', g);
      const l = line(a, b, 'g-line', g);
      l.setAttribute('marker-start', arrow);
      l.setAttribute('marker-end', arrow);
      label(add(mid(a, b), n, 44), L.ht_length, g);
    });
    dim('ht_angle', (g) => {
      const c = p.axis;
      path(arc(c, 92, Math.PI, Math.PI - p.ha), 'g-line', g);
      const a = Math.PI - p.ha / 2;
      label({ x: c.x + 142 * Math.cos(a), y: c.y + 142 * Math.sin(a) }, L.ht_angle, g);
    });
    dim('st_angle', (g) => {
      const c = p.seatAtDrop;
      path(arc(c, 92, Math.PI, Math.PI - p.sa), 'g-line', g);
      const a = Math.PI - p.sa / 2;
      label({ x: c.x + 142 * Math.cos(a), y: c.y + 142 * Math.sin(a) }, L.st_angle, g);
    });
    dim('st_length', (g) => {
      const n = { x: -Math.sin(p.sa), y: -Math.cos(p.sa) };
      const a = add(p.BB, n, 74);
      const b = add(p.S, n, 74);
      line(add(p.BB, n, 26), add(a, n, 14), 'g-ext', g);
      line(add(p.S, n, 20), add(b, n, 14), 'g-ext', g);
      const l = line(a, b, 'g-line', g);
      l.setAttribute('marker-start', arrow);
      l.setAttribute('marker-end', arrow);
      label(add(mid(a, b), n, 44), L.st_length, g);
    });
    const below = (a, b) => {
      const u = unit(a, b);
      let n = { x: -u.y, y: u.x };
      if (n.y > 0) n = { x: -n.x, y: -n.y };
      return n;
    };
    dim('chainstay', (g) => {
      const n = below(p.BB, p.RA);
      const a = add(p.BB, n, 72);
      const b = add(p.RA, n, 72);
      line(add(p.BB, n, 28), add(a, n, 14), 'g-ext', g);
      line(add(p.RA, n, 14), add(b, n, 14), 'g-ext', g);
      const l = line(a, b, 'g-line', g);
      l.setAttribute('marker-start', arrow);
      l.setAttribute('marker-end', arrow);
      label(add(mid(a, b), n, 42), L.chainstay, g);
    });
    dim('front_centre', (g) => {
      const n = below(p.BB, p.FA);
      const a = add(p.BB, n, 72);
      const b = add(p.FA, n, 72);
      line(add(p.BB, n, 28), add(a, n, 14), 'g-ext', g);
      line(add(p.FA, n, 14), add(b, n, 14), 'g-ext', g);
      const l = line(a, b, 'g-line', g);
      l.setAttribute('marker-start', arrow);
      l.setAttribute('marker-end', arrow);
      label(add(mid(a, b), n, 42), L.front_centre, g);
    });
    dim('wheelbase', (g) => {
      const y = p.drop - p.R - 56;
      line(p.RA, { x: p.RA.x, y: y - 16 }, 'g-ext', g);
      line(p.FA, { x: p.FA.x, y: y - 16 }, 'g-ext', g);
      const l = line({ x: p.RA.x, y }, { x: p.FA.x, y }, 'g-line', g);
      l.setAttribute('marker-start', arrow);
      l.setAttribute('marker-end', arrow);
      label({ x: (p.RA.x + p.FA.x) / 2, y: y - 42 }, L.wheelbase, g);
    });
    dim('bb_drop', (g) => {
      const x = 92;
      line({ x: 30, y: 0 }, { x: x + 14, y: 0 }, 'g-ext', g);
      const l = line({ x, y: 0 }, { x, y: p.drop }, 'g-line', g);
      l.setAttribute('marker-start', arrow);
      l.setAttribute('marker-end', arrow);
      label({ x: x + 46, y: p.drop / 2 - 6 }, L.bb_drop, g);
    });
    dim('fork_offset', (g) => {
      line(p.foot, p.FA, 'g-line', g).setAttribute('marker-end', arrow);
      label(add(p.FA, { x: 0.7, y: -0.7 }, 64), L.fork_offset, g);
    });

    if (this.hl) this.highlight(this.hl);
  }

  /* ---------- Phones: the full table, full screen ---------- */

  setupSheet() {
    const sheet = this.querySelector('[data-geo-sheet]');
    const button = this.querySelector('[data-geo-sheet-open]');
    const wrap = this.querySelector('.b-geo__table-wrap');
    if (!sheet || !button || !wrap) return;
    if (typeof sheet.showModal !== 'function') {
      // Very old browsers keep the table on the page.
      this.classList.add('is-plain');
      return;
    }
    const body = sheet.querySelector('[data-geo-sheet-body]');
    const home = wrap.parentNode;
    const after = wrap.nextSibling;
    button.addEventListener('click', () => {
      wrap.classList.add('is-in');
      body.append(wrap);
      sheet.showModal();
      document.documentElement.classList.add('b-geo-sheet-open');
      // Columns snap clear of the pinned names. Start at the top, the chosen size beside the one
      // below it.
      const name = wrap.querySelector('thead .b-geo__name');
      const names = name?.offsetWidth || 0;
      wrap.style.scrollPaddingLeft = `${names}px`;
      wrap.scrollTop = 0;
      const first = wrap.querySelector(`thead [data-col="${Math.max(0, this.size - 1)}"]`);
      wrap.scrollLeft = first ? Math.max(0, first.offsetLeft - names) : 0;
    });
    sheet.querySelector('[data-geo-sheet-close]')?.addEventListener('click', () => sheet.close());
    sheet.addEventListener('close', () => {
      home.insertBefore(wrap, after);
      wrap.style.scrollPaddingLeft = '';
      wrap.scrollLeft = 0;
      document.documentElement.classList.remove('b-geo-sheet-open');
      button.focus({ preventScroll: true });
    });
  }

  /* ---------- Find your size ---------- */

  setupFinder() {
    const input = this.querySelector('[data-geo-height]');
    if (!input) return;
    const ranges = [...this.table.querySelectorAll('td[data-range]')].map((td) =>
      td.dataset.range.split(/[-–]/).map((n) => parseFloat(n))
    );
    const finder = this.querySelector('[data-geo-finder]');
    const out = this.querySelector('[data-geo-suggest]');
    const text = this.querySelector('[data-geo-suggest-text]');
    const show = this.querySelector('[data-geo-show]');
    const sizes = this.cfg.sizes || [];
    input.addEventListener('input', () => {
      const h = parseFloat(input.value);
      if (!Number.isFinite(h) || h < 100 || h > 250) {
        out.hidden = true;
        finder?.classList.remove('is-answered');
        return;
      }
      const hits = ranges.map((r, i) => (h >= r[0] && h <= r[1] ? i : -1)).filter((i) => i >= 0);
      out.hidden = false;
      finder?.classList.add('is-answered');
      this.pick = hits;
      show.hidden = hits.length === 0;
      if (!hits.length) text.textContent = this.cfg.outside || '';
      else if (hits.length > 1) text.textContent = (this.cfg.between || '').replace('[size]', sizes[hits[0]]).replace('[size2]', sizes[hits[1]]);
      else text.textContent = (this.cfg.suggest || '').replace('[size]', sizes[hits[0]]);
    });
    show.addEventListener('click', () => {
      if (!this.pick?.length) return;
      this.setCompare(this.pick.length > 1 ? this.pick[1] : null);
      this.setSize(this.pick[0]);
      this.querySelector('.b-geo__controls')?.scrollIntoView({ behavior: reduce.matches ? 'auto' : 'smooth', block: 'start' });
    });
  }
}

if (!customElements.get('bossi-geometry')) customElements.define('bossi-geometry', BossiGeometry);
